import { combineReducers } from '@ngrx/store';
import { reducer as academicStudents } from './academic-students-reducer';
import { reducer as activities } from './activities-reducer';
import { reducer as schoolAssessments } from './assessments-reducer/school-assessments-reducer';
import { reducer as studentAssessments } from './assessments-reducer/student-assessments-reducer';
import { reducer as attendanceStudents } from './attendance-students-reducer';
import { reducer as batchActions } from './batch-actions-reducer';
import { reducer as calculatedFields } from './calculated-fields-reducer';
import { reducer as careerPath } from './career-path-reducers';
import { reducer as collegePath } from './college-path-reducers';
import { reducer as csvData } from './csv-data-reducer';
import { reducer as currentUser } from './current-user-reducer';
import { reducer as dashboard } from './dashboard-reducer';
import { reducer as dataLoads } from './data-loads-reducer';
import { reducer as docLogs } from './doc-logs-reducer';
import { reducer as flags } from './flags-reducer';
import { reducer as focus } from './focus-reducer/focus-reducer';
import { reducer as madlibOptions } from './madlib-options-reducer';
import { reducer as metrics } from './metrics-reducer';
import { reducer as milestones } from './milestone-reducer';
import { reducer as myStudents } from './my-students-reducer';
import { reducer as networkDashboard } from './network-dashboard-reducer/network-dashboard-reducer';
import { reducer as partnerOrgs } from './partner-orgs-reducer';
import { reducer as patchProcessing } from './patch-processing-reducer';
import { reducer as postsecondaryStudents } from './postsecondary-students-reducer';
import { reducer as configs } from './previous-states-reducer';
import { reducer as rollupGrouping } from './rollup-grouping-reducer/rollup-grouping-reducer';
import { reducer as school } from './school-reducer';
import { reducer as shelterData } from './shelter-reducer/shelter-reducer';
import { reducer as studentActivities } from './student-activities-reducer';
import { reducer as studentGroupings } from './student-groupings-reducer/student-groupings-reducer';
import { reducer as studentPaths } from './student-paths-reducer';
import { reducer as student } from './student-reducer';
import { reducer as studentSupports } from './student-supports-reducer';
import { reducer as tiles } from './tile-reducer/tile-reducer';
import { reducer as toggles } from './toggle-reducer';
import { reducer as tools } from './tools-reducer';
import { reducer as users } from './users-reducer';

export const reducers = combineReducers({
  school,
  attendanceStudents,
  myStudents,
  flags,
  academicStudents,
  postsecondaryStudents,
  toggles,
  docLogs,
  currentUser,
  dashboard,
  studentSupports,
  madlibOptions,
  milestones,
  users,
  studentPaths,
  collegePath,
  careerPath,
  batchActions,
  configs,
  tools,
  calculatedFields,
  tiles,
  focus,
  rollupGrouping,
  studentGroupings,
  student,
  csvData,
  activities,
  studentActivities,
  partnerOrgs,
  patchProcessing,
  metrics,
  schoolAssessments,
  studentAssessments,
  networkDashboard,
  shelterData,
  dataLoads,
});

export const initialState = {
  PORTAL_STORE: {
    school: {
      loaded: false,
      loading: false,
      school: {},
    },
    student: {
      loaded: false,
      loading: false,
      studentEntities: {},
    },
    patchProcessing: {
      patchProcessing: false,
    },
    attendanceStudents: {
      loaded: false,
      loading: false,
      loadedProjection: {},
      studentEntities: {},
      sort: {},
      dimensions: {},
    },
    myStudents: {
      loaded: false,
      loading: false,
      loadedProjection: {},
      studentEntities: {},
      sort: {},
      dimensions: {},
    },
    postsecondaryStudents: {
      loaded: false,
      loading: false,
      loadedProjection: {},
      studentEntities: {},
      sort: {},
      dimensions: {},
    },
    calculatedFields: {
      loaded: false,
      loading: false,
      calculatedFieldsEntities: {},
    },
    toggles: {
      loading: false,
      toggleEntities: {},
    },
    flags: {
      loaded: false,
      loading: false,
      flagEntities: {},
    },
    docLogs: {
      loaded: false,
      loading: false,
      docLogsEntities: {},
    },
    currentUser: {
      loading: false,
      loaded: false,
      userEntity: null,
    },
    dashboard: {
      madlib: {},
      students: {
        studentEntities: {},
        loading: false,
        loaded: false,
      },
      studentSupports: {
        studentSupportEntities: {},
        loading: false,
        loaded: false,
      },
      graphs: {
        // Attendance
        // Post-secondary
        POST_SECONDARY_FLAGS: {
          loading: false,
          loaded: false,
          data: null,
        },
        POST_SECONDARY_PLANS: {
          loading: false,
          loaded: false,
          data: null,
        },
        // GRAPH STATE IS CALCULATED ON THE FRONTEND
        // NO ENDPOINT EXISTS FOR THIS GRAPH
        FAILURES_WITH_NO_SUPPORT: {
          loading: false,
          loaded: false,
          data: null,
        },
      },
    },
    studentSupports: {
      loaded: false,
      loading: false,
      studentSupportsEntities: {},
    },
    madlibOptions: {
      loaded: false,
      loading: false,
      madlibOptionsEntity: null,
    },
    milestones: {
      loaded: false,
      loading: false,
      allLoaded: false,
      loadedEntities: {},
      milestoneEntities: {},
    },
    users: {
      loaded: false,
      loading: false,
      userEntities: {},
    },
    studentPaths: {
      loaded: false,
      loading: false,
      allLoaded: false,
      loadedEntities: {},
      studentPathsEntities: {},
    },
    collegePath: {
      loaded: false,
      loading: false,
      collegePathEntities: {},
    },
    careerPath: {
      loaded: false,
      loading: false,
      careerPathEntities: {},
    },
    batchActions: {
      enabled: false,
      selectedStudentIds: [],
    },
    // store slice to house app related configuration settings
    configs: {
      currentGrouping: null,
      // all states a user has navigated to during session, from first to last, except for current state (CM)
      previousStates: [],
    },
    tools: {
      loaded: false,
      loading: false,
      toolsEntities: {},
    },
    tiles: {
      loaded: false,
      loading: false,
      // todo: tileData
      tileList: [],
    },
    focus: {
      loaded: false,
      loading: false,
      focusData: {},
    },
    rollupGrouping: {
      loaded: false,
      loading: false,
      groupingData: {},
    },
    studentGroupings: {
      loaded: false,
      loading: false,
      studentGroupingsData: [],
    },
    csvData: {
      loaded: false,
      loading: false,
      listData: {},
      madLibSelections: {
        Dimension1: null,
        Dimension2: null,
        Groupings: null,
      },
    },
    activities: {
      loaded: false,
      loading: false,
      activitiesEntities: {},
    },
    studentActivities: {
      loaded: false,
      loading: false,
      studentActivitiesEntities: {},
    },
    partnerOrgs: {
      loaded: false,
      loading: false,
      partnerOrgsEntities: {},
    },
    metrics: {
      changeMade: false,
    },
    schoolAssessments: {
      loaded: false,
      loading: false,
      schoolAssessmentsEntities: {},
    },
    studentAssessments: {
      loaded: false,
      loading: false,
      studentAssessmentsEntities: {},
    },
    networkDashboard: {
      initialFilters: {
        loaded: false,
        data: [],
      },
      initialParams: {
        loaded: false,
        data: [],
      },
      tileData: {
        loaded: false,
        data: {},
      },
    },
    dataLoads: {
      loaded: false,
      loading: false,
      dataLoadsEntities: {},
    },
    shelterData: {
      loaded: false,
      loading: false,
      shelterId: null,
      columnDefs: [],
      rowData: [],
    },
  },
};
